import { useLocation } from '@tanstack/react-router';
import { AnimatePresence, motion } from 'motion/react';
import * as React from 'react';
import styled from 'styled-components';

import * as icons from '@creditornot/alchemy-react/icons';
import { IconButton, TextButton } from '@creditornot/cb-button';
import { elevationClassName } from '@creditornot/cb-ingredients';
import { color } from '@creditornot/cb-ingredients/design-tokens';
import { Textarea } from '@creditornot/cb-input';

import { useCreateFeedbackMutation, useUpdateFeedbackMutation } from './mutation';
import { RatingInput } from './RatingInput';
import {
  OperationType,
  telemetryFeedbackToastData,
  telemetryFeedbackToastInteractionClose,
  telemetryFeedbackToastInteractionComment,
  telemetryFeedbackToastInteractionRating,
  telemetryFeedbackToastInteractionSubmit,
  useTelemetryFeedbackToastView,
} from './telemetry';
import { FeedbackForm, FeedbackResponse } from '../../../api-types/director';
import { useFormatMessage } from '../../../hooks/useFormatMessage';
import { media } from '../../../media';
import { LocalizedMessageId } from '../../../types';
import { ApiErrorNotification } from '../ApiErrorNotification';
import { Stack } from '../Stack';
import { SubmitStateBadge } from '../SubmitStateBadge';
import { Typography as T } from '../Typography/Typography';

const StyledElevation = styled.div.attrs({ className: elevationClassName.medium })`
  width: 100%;
  pointer-events: all;
  padding: 16px;
  border-radius: 8px;

  @media ${media.sm} {
    width: 380px;
  }
`;

const Header = styled(Stack)`
  width: 100%;
`;

const Subtitle = styled(T.footnote)`
  display: block;
  color: ${color.textSubdued};
  margin-top: 4px;
  margin-bottom: 16px;
`;

const Description = styled(T.caption)`
  margin-top: 16px;
  display: block;
  min-height: 40px;
  @media ${media.sm} {
    min-height: 20px;
  }
`;

const Footer = styled(Stack)`
  margin-top: 16px;
`;

type Props = {
  title?: string;
  operationType: OperationType;
  onSuccess?: (params: FeedbackResponse) => void;
  onDismiss: () => void;
  warehouseId: string;
};

const GOOD_STARS_RATING = 3;
const TOTAL_STARS_RATING = 5;
const TOAST_CLOSE_TIMEOUT_MS = 900;

function ratingToMessageId(rating: number): LocalizedMessageId {
  if (rating <= GOOD_STARS_RATING) {
    return 'components.feedback.how_can_we_improve_it';
  } else {
    return 'components.feedback.can_we_make_it_better';
  }
}

export const FeedbackToast: React.FC<Props> = ({
  onSuccess,
  onDismiss,
  title,
  operationType,
  warehouseId,
}) => {
  const format = useFormatMessage();

  useTelemetryFeedbackToastView(operationType, warehouseId, !!warehouseId);

  const [isExpanded, setExpanded] = React.useState(false);
  const [rating, setRating] = React.useState(0);
  const [message, setMessage] = React.useState('');
  const [feedbackId, setFeedbackId] = React.useState<string | null>(null);

  const { pathname, searchStr } = useLocation();

  const feedbackPath = `${pathname}${searchStr}`;

  const { mutate: mutateCreate, error: createError } = useCreateFeedbackMutation((result) => {
    setFeedbackId(result.feedbackId);
  });

  const {
    mutate: mutateUpdate,
    isPending,
    isSuccess,
    error: updateError,
  } = useUpdateFeedbackMutation((result) => {
    setTimeout(() => {
      onSuccess?.(result);
    }, TOAST_CLOSE_TIMEOUT_MS);
  });

  const error = createError || updateError;

  function sendFeedback(values: FeedbackForm) {
    const feedback: FeedbackForm = {
      rating: values.rating,
      message: values.message || undefined,
      feedbackPath: values.feedbackPath,
    };

    if (feedbackId) {
      mutateUpdate({ feedbackId, ...feedback });
    } else {
      mutateCreate(feedback);
    }
  }

  function handleRatingChange(value: number) {
    setRating(value);
    setExpanded(true);
    sendFeedback({ feedbackPath, rating: value, message });

    telemetryFeedbackToastInteractionRating({
      operationType,
      wmVenueId: warehouseId,
      operationRating: value,
    });
  }

  function handleMessageChange(event: React.ChangeEvent<HTMLTextAreaElement>) {
    setMessage(event.target.value);
  }

  function handleSubmitButtonClick() {
    sendFeedback({ feedbackPath, rating, message });
    telemetryFeedbackToastInteractionSubmit({ operationType, wmVenueId: warehouseId });
    telemetryFeedbackToastData({
      operationType,
      wmVenueId: warehouseId,
      operationComment: message,
      operationRating: rating,
    });
  }

  const handleToastClose = () =>
    telemetryFeedbackToastInteractionClose({ operationType, wmVenueId: warehouseId });

  return (
    <StyledElevation data-test-id="feedback-toast">
      {isSuccess || isPending ? (
        <Stack alignItems="center">
          <SubmitStateBadge isLoading={isPending} hideText={true} />
        </Stack>
      ) : (
        <>
          <Header direction="row" alignItems="flex-start" justifyContent="space-between">
            <T.captionLabel>{title ?? format('components.feedback.title')}</T.captionLabel>
            <IconButton
              aria-label={format('components.feedback.close_button_label')}
              variant="neutral"
              size="small"
              alignment="iconEdges"
              background="transparent"
              onClick={() => {
                onDismiss();
                handleToastClose();
              }}
              data-test-id="close-feedback-toast-button"
              icon={icons.cross}
            />
          </Header>
          <Subtitle>{format('components.feedback.subtitle')}</Subtitle>
          <RatingInput
            starsCount={TOTAL_STARS_RATING}
            value={rating}
            onChange={handleRatingChange}
          />
          <AnimatePresence>
            {isExpanded && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
              >
                <Stack gap="regularMedium">
                  <Description>{format(ratingToMessageId(rating))}</Description>
                  <Textarea
                    value={message}
                    onChange={handleMessageChange}
                    disableHorizontalResize
                    placeholder={format('components.feedback.placeholder')}
                    onClick={() =>
                      telemetryFeedbackToastInteractionComment({
                        operationType,
                        wmVenueId: warehouseId,
                      })
                    }
                  />
                  <ApiErrorNotification error={error} />
                </Stack>
                <Footer gap="regularMedium" direction="row" justifyContent="flex-end">
                  <TextButton onClick={handleSubmitButtonClick}>
                    {format('components.feedback.submit')}
                  </TextButton>
                </Footer>
              </motion.div>
            )}
          </AnimatePresence>
        </>
      )}
    </StyledElevation>
  );
};
